"use client";

import React from "react";
import { Phone, Mail, MapPin, CircleAlert, Loader, CircleCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function SupportForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  const inputBaseClasses = "w-full rounded-md border border-border/40 bg-[#111418] px-4 py-3 text-foreground placeholder:text-muted-foreground/50 focus:border-secondary focus:outline-none focus:ring-2 focus:ring-secondary/50 transition-all duration-300";
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-6 rounded-xl border border-border/20 bg-card/30 p-12 text-center backdrop-blur-sm">
        <div className="rounded-full bg-green-500/20 p-4 text-green-400 shadow-[0_0_30px_rgba(34,197,94,0.3)]">
          <CircleCheck className="size-12" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-foreground">Žádost přijata</h3>
          <p className="text-muted-foreground">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-border/50 hover:bg-accent hover:text-accent-foreground">
          Odeslat další žádost
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">Vaše jméno</FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="Jan Novák" className={inputBaseClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">E-mailová adresa</FormLabel>
              <FormControl>
                <input {...field} type="email" placeholder="jan@priklad.cz" className={inputBaseClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^\+?[0-9\s\-\(\)]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">Telefonní číslo</FormLabel>
              <FormControl>
                <input {...field} type="tel" placeholder="+420 123 456 789" className={inputBaseClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="equipment_type" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">Typ zařízení</FormLabel>
              <FormControl>
                <select {...field} className={inputBaseClasses}>
                  <option value="" disabled>
                    Vyberte zařízení
                  </option>
                  {["Klimatizace", "Tepelné čerpadlo", "Termostat", "Ventilační systém", "Jiné"].map((opt, i) => <option key={opt} value={opt} data-index={i}>
                      {opt}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="issue_description" rules={{
      required: "Popište prosím problém",
      minLength: {
        value: 20,
        message: "Description must be at least 20 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground">Issue Description</FormLabel>
            <FormControl>
              <textarea {...field} placeholder="Popište problém, se kterým se potýkáte..." className={`${inputBaseClasses} min-h-[120px] resize-y`} />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground/70">
              Uveďte prosím co nejvíce podrobností, abychom mohli problém co nejrychleji diagnostikovat.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="space-y-4 rounded-lg border border-border/20 bg-card/20 p-5">
        <FormField control={control} name="urgency" rules={{
        required: "Please select an urgency level"
      }} render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="text-muted-foreground">Úroveň naléhavosti</FormLabel>
              <FormControl>
                <div className="flex flex-wrap gap-4">
                  {["Nízká", "Střední", "Vysoká", "Kritická"].map((opt, i) => <label key={opt} data-index={i} className={`flex cursor-pointer items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${field.value === opt ? "border-secondary bg-secondary/10 text-secondary" : "border-border/40 bg-transparent text-muted-foreground hover:border-border hover:bg-accent/50"}`}>
                      <input type="radio" className="sr-only" {...field} value={opt} checked={field.value === opt} />
                      {opt === "Kritická" && <CircleAlert className="size-4" />}
                      {opt}
                    </label>)}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="callback_requested" render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md p-2">
            <FormControl>
              <div className="flex h-5 items-center">
                <input type="checkbox" className="size-4 rounded border-border/50 bg-[#111418] text-secondary focus:ring-2 focus:ring-secondary/50 focus:ring-offset-0 focus:ring-offset-transparent" checked={field.value} onChange={field.onChange} onBlur={field.onBlur} name={field.name} ref={field.ref} />
              </div>
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="cursor-pointer text-sm font-medium text-foreground">
                Požádat o zpětné volání
              </FormLabel>
              <FormDescription className="text-xs text-muted-foreground/70">
                Zaškrtněte, pokud si přejete, abychom vám co nejdříve zavolali zpět.
              </FormDescription>
            </div>
          </FormItem>} />

      {submitError && <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-secondary py-6 text-lg font-semibold text-secondary-foreground shadow-[0_0_20px_rgba(var(--color-secondary),0.3)] hover:bg-secondary/90 hover:shadow-[0_0_30px_rgba(var(--color-secondary),0.5)] transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Zpracování žádosti...
          </> : "Odeslat žádost o servis"}
      </Button>
    </Form>;
}
export default function ImmediateTechnicalSupport() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="immediate-technical-support" ref={sectionRef} className="dark relative min-h-screen overflow-hidden bg-background py-24 text-foreground">
      {}
      <div className="group absolute inset-0 z-0">
        <div className="absolute inset-0 bg-background/90 z-10 transition-opacity duration-700 group-hover:bg-background/80" />
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582043/img/nexo_tech_service_map_bg.png')] bg-cover bg-center bg-no-repeat opacity-10 grayscale transition-all duration-1000 ease-in-out group-hover:opacity-30 group-hover:grayscale-0" aria-hidden="true" />
      </div>

      <div className="container relative z-20 mx-auto max-w-screen-xl px-6">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          
          {}
          <div className={`flex flex-col justify-center space-y-12 lg:col-span-5 transition-all duration-1000 ${isVisible ? "translate-x-0 opacity-100" : "-translate-x-12 opacity-0"}`}>
            <div className="space-y-6">
              <div className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary shadow-[0_0_15px_rgba(var(--color-primary),0.2)]">
                <span className="relative mr-2 flex size-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex size-2 rounded-full bg-primary"></span>
                </span>
                24/7 Pohotovostní dispečink
              </div>
              <h2 className="text-4xl font-extrabold tracking-tight text-foreground md:text-5xl lg:text-6xl">
                Okamžitá <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                  technická podpora
                </span>
              </h2>
              <p className="max-w-md text-lg text-muted-foreground">
                Náš tým techniků je v pohotovosti. Kontaktujte nás přímo nebo odešlete podrobnou žádost pro rychlou diagnostiku a nasazení.
              </p>
            </div>

            <div className="space-y-8">
              {}
              <div className="group/stat flex items-center gap-6">
                <div className="relative flex size-16 shrink-0 items-center justify-center rounded-2xl border border-primary/20 bg-primary/5 transition-all duration-500 group-hover/stat:border-primary/50 group-hover/stat:bg-primary/10">
                  <div className="absolute inset-0 rounded-2xl bg-primary/20 opacity-0 blur-xl transition-opacity duration-500 group-hover/stat:opacity-100" />
                  <Phone className="relative z-10 size-7 text-primary drop-shadow-[0_0_10px_rgba(var(--color-primary),0.8)] transition-transform duration-500 group-hover/stat:scale-110" />
                </div>
                <div>
                  <p className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
                    Přímá linka dispečinku
                  </p>
                  <p className="mt-1 text-2xl font-bold text-foreground tracking-tight">
                    +1 (555) 987-6543
                  </p>
                </div>
              </div>

              {}
              <div className="group/stat flex items-center gap-6">
                <div className="relative flex size-16 shrink-0 items-center justify-center rounded-2xl border border-primary/20 bg-primary/5 transition-all duration-500 group-hover/stat:border-primary/50 group-hover/stat:bg-primary/10">
                  <div className="absolute inset-0 rounded-2xl bg-primary/20 opacity-0 blur-xl transition-opacity duration-500 group-hover/stat:opacity-100" />
                  <Mail className="relative z-10 size-7 text-primary drop-shadow-[0_0_10px_rgba(var(--color-primary),0.8)] transition-transform duration-500 group-hover/stat:scale-110" />
                </div>
                <div>
                  <p className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
                    Technická podpora
                  </p>
                  <p className="mt-1 text-xl font-bold text-foreground tracking-tight">
                    contact@nexotechhvac.com
                  </p>
                </div>
              </div>

              {}
              <div className="group/stat flex items-center gap-6">
                <div className="relative flex size-16 shrink-0 items-center justify-center rounded-2xl border border-primary/20 bg-primary/5 transition-all duration-500 group-hover/stat:border-primary/50 group-hover/stat:bg-primary/10">
                  <div className="absolute inset-0 rounded-2xl bg-primary/20 opacity-0 blur-xl transition-opacity duration-500 group-hover/stat:opacity-100" />
                  <MapPin className="relative z-10 size-7 text-primary drop-shadow-[0_0_10px_rgba(var(--color-primary),0.8)] transition-transform duration-500 group-hover/stat:scale-110" />
                </div>
                <div>
                  <p className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
                    Centrála
                  </p>
                  <p className="mt-1 text-lg font-medium text-foreground">
                    456 Elm Street, Suite 300<br />
                    VašeMěsto, VášKraj, PSČ
                  </p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className={`lg:col-span-7 transition-all duration-1000 delay-200 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
            <div className="relative overflow-hidden rounded-2xl border border-border/30 bg-card/40 p-8 shadow-2xl backdrop-blur-md sm:p-10">
              {}
              <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
              
              <div className="relative z-10">
                <div className="mb-8 border-b border-border/40 pb-6">
                  <h3 className="text-2xl font-bold text-foreground">
                    Portál pro žádosti o servis
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Vyplňte níže uvedený formulář pro vytvoření tiketu technické podpory. Náš systém vaši žádost předá příslušnému týmu techniků.
                  </p>
                </div>

                <FormProvider formId="technical_support_form_home" sectionName="Immediate Technical Support" defaultValues={{
                name: "",
                email: "",
                phone: "",
                equipment_type: "",
                issue_description: "",
                urgency: "Medium",
                callback_requested: false
              }} submitText="Odeslat žádost o servis" successMessage="Vaše žádost o technickou podporu byla úspěšně zaznamenána. Technik vaši žádost prověří a brzy vás bude kontaktovat.">
                  <SupportForm />
                </FormProvider>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}